import apiService from './api.service';
import { MarkerData } from '../types/markerData';

class PinsService {
  // Cache per i markers
  private markersCache: Map<string, { data: MarkerData[]; timestamp: number }> = new Map();
  private CACHE_DURATION = 5 * 60 * 1000; // 5 minuti

  // Ottieni tutti i markers dell'utente
  async getUserMarkers(userId: string, forceRefresh = false): Promise<MarkerData[]> {
    const cacheKey = `markers_${userId}`;
    
    if (!forceRefresh) {
      const cached = this.markersCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        // Assicurati che i dati in cache siano sempre un array valido
        return Array.isArray(cached.data) ? cached.data : [];
      }
    }

    const response = await apiService.get<MarkerData[]>(`/pins/user/${userId}`);
    
    // Assicurati che la risposta sia sempre un array valido
    const markersData = Array.isArray(response.data) ? response.data : [];
    
    // Salva in cache
    this.markersCache.set(cacheKey, {
      data: markersData,
      timestamp: Date.now()
    });

    return markersData;
  }

  // Crea un nuovo marker
  async createMarker(markerData: Partial<MarkerData>): Promise<MarkerData> {
    const response = await apiService.post<MarkerData>('/pins/', markerData);
    
    // Invalida la cache dopo la creazione
    const userId = sessionStorage.getItem('userId');
    if (userId) {
      this.markersCache.delete(`markers_${userId}`);
    }

    return response.data;
  }

  // Aggiorna un marker esistente
  async updateMarker(markerId: string | number, markerData: Partial<MarkerData>): Promise<MarkerData> {
    const response = await apiService.patch<MarkerData>(`/pins/${markerId}/`, markerData);
    
    // Invalida la cache dopo l'aggiornamento
    const userId = sessionStorage.getItem('userId');
    if (userId) {
      this.markersCache.delete(`markers_${userId}`);
    }

    return response.data;
  }

  // Elimina un marker
  async deleteMarker(markerId: string | number): Promise<void> {
    await apiService.delete(`/pins/${markerId}/`);
    
    // Invalida la cache dopo l'eliminazione
    const userId = sessionStorage.getItem('userId');
    if (userId) {
      this.markersCache.delete(`markers_${userId}`);
    }
  }

  // Ottieni un singolo marker
  async getMarker(markerId: string | number): Promise<MarkerData> {
    const response = await apiService.get<MarkerData>(`/pins/${markerId}/`);
    return response.data;
  }

  // Upload immagine per un marker
  async uploadMarkerImage(markerId: string | number, file: File): Promise<MarkerData> {
    const formData = new FormData();
    formData.append('photo', file);

    const response = await apiService.patch<MarkerData>(`/pins/${markerId}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // Invalida la cache
    const userId = sessionStorage.getItem('userId');
    if (userId) {
      this.markersCache.delete(`markers_${userId}`);
    }

    return response.data;
  }

  // Invalida la cache
  clearCache(userId?: string): void {
    if (userId) {
      this.markersCache.delete(`markers_${userId}`);
    } else {
      this.markersCache.clear();
    }
  }

  // Metodo helper per ottenere i markers dell'utente corrente
  async getCurrentUserMarkers(forceRefresh = false): Promise<MarkerData[]> {
    const userId = sessionStorage.getItem('userId');
    if (!userId) {
      throw new Error('User not authenticated');
    }
    return this.getUserMarkers(userId, forceRefresh);
  }

  // Batch operations per ottimizzare le performance
  async batchCreateMarkers(markers: Partial<MarkerData>[]): Promise<MarkerData[]> {
    const promises = markers.map(marker => this.createMarker(marker));
    const results = await Promise.all(promises);
    
    // Invalida la cache una sola volta alla fine
    const userId = sessionStorage.getItem('userId');
    if (userId) {
      this.markersCache.delete(`markers_${userId}`);
    }

    return results;
  }

  async batchDeleteMarkers(markerIds: (string | number)[]): Promise<void> {
    const promises = markerIds.map(id => this.deleteMarker(id));
    await Promise.all(promises);
    
    // Invalida la cache una sola volta alla fine
    const userId = sessionStorage.getItem('userId');
    if (userId) {
      this.markersCache.delete(`markers_${userId}`);
    }
  }
}

// Esporta istanza singleton
const pinsService = new PinsService();
export default pinsService;
